import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

// Android hardware / gesture "Back".
//
// iOS has no hardware back button — there the left-edge swipe is handled by
// useEdgeSwipeBack. This is its Android counterpart: it intercepts the system
// back event and drives the SAME history.back() the edge-swipe uses, so React
// Router + useViewTransitionLocation run the identical directional slide. When
// there's nowhere left to go back to (the user is at the first screen), we hand
// control back to the OS and let it minimise/close the app — the behaviour
// Android users expect, instead of the WebView's default of trapping them.
//
// Android-only: the @capacitor/app backButton event never fires on iOS, but we
// also gate on the platform so the plugin is dynamically imported and never
// enters the web bundle's critical path (mirrors nativeSocialLogin.js).
export default function useAndroidBackButton() {
  useEffect(() => {
    if (Capacitor?.getPlatform?.() !== 'android') return undefined;

    let removeListener;
    let cancelled = false;

    import('@capacitor/app')
      .then(({ App }) => {
        const handlePromise = App.addListener('backButton', ({ canGoBack }) => {
          // Overlays (booking wizard, specialist drawer) are real history
          // entries (?sheet=…&bstep=N), so history.back() steps through them one
          // at a time — no special-casing here. See utils/overlayNav.js.
          // Gate strictly on canGoBack — the WebView's real position in its
          // history. Do NOT also fall back to `window.history.length > 1`:
          // in an SPA history.length only ever GROWS (it never shrinks when
          // you go back), so once the user has opened any second screen it
          // stays > 1 for the rest of the session. That made App.exitApp()
          // unreachable — at the first screen canGoBack is false but
          // length > 1, so back fired a no-op history.back() instead of
          // minimising the app, trapping the user (the exact thing this hook
          // exists to prevent). React Router's pushState navigations are
          // tracked by the WebView, so canGoBack alone reflects them correctly.
          if (canGoBack) {
            window.history.back();
          } else {
            App.exitApp();
          }
        });
        // If the effect already cleaned up before the listener resolved, remove
        // it immediately so we never leak a duplicate listener.
        handlePromise.then((handle) => {
          if (cancelled) handle.remove();
          else removeListener = () => handle.remove();
        });
      })
      .catch(() => {
        /* plugin unavailable — nothing to wire up */
      });

    return () => {
      cancelled = true;
      if (removeListener) removeListener();
    };
  }, []);
}
